/**
 * Types for the custom homepage API response
 */

import { Navigation } from '../assistant/interfaces/Navigation';

//everything is optional, because homepages may be drafts.
interface BaseModuleAttributes {
  name?: string;
  action?: Navigation;
}

export interface ContentModuleSettings {
  content_inclusion?: 'all' | 'user-generated' | 'admin-generated';
  title?: string;
  description?: string;
  display_type?: 'row' | 'grid' | 'hero' | 'gallery';
  minimum_content_threshold?: number;
  name?: string;
  only_include_followed_topics?: boolean;
  source?:
    | 'topic'
    | 'initiative'
    | 'for_you'
    | 'featured'
    | 'trending'
    | 'latest';
  reference_ids?: number[];
}

export interface BannerModuleSettings {
  aria_label?: string;
  image_url?: string;
  mobile_image?: string;
  size?: 'ExtraSmall' | 'Small' | 'Medium' | 'Large';
  web_image?: string;
}

export interface ShortcutItem {
  // Placeholder - define properties based on actual shortcut item structure
  // right now, none of these are used. only presence of the module is checked.
  [key: string]: unknown;
}

export interface ShortcutsModuleSettings {
  icon_url?: string | null;
  shortcuts?: ShortcutItem[];
  title?: string | null;
}

export interface ContentModuleAttributes extends BaseModuleAttributes {
  settings: ContentModuleSettings;
}
export interface BannerModuleAttributes extends BaseModuleAttributes {
  settings: BannerModuleSettings;
}
export interface ShortcutsModuleAttributes extends BaseModuleAttributes {
  settings: ShortcutsModuleSettings;
}

interface BaseHomepageModule {
  id: string;
  type: string;
}

export interface ContentHomepageModule extends BaseHomepageModule {
  type: 'content';
  attributes: ContentModuleAttributes;
}

export interface BannerHomepageModule extends BaseHomepageModule {
  type: 'banner';
  attributes: BannerModuleAttributes;
}

export interface ShortcutsHomepageModule extends BaseHomepageModule {
  type: 'shortcuts';
  attributes: ShortcutsModuleAttributes;
}

export type HomepageModule =
  | ContentHomepageModule
  | BannerHomepageModule
  | ShortcutsHomepageModule;

export interface HomepageAttributes {
  id: string;
  name?: string;
  recommended_section_enabled?: boolean;
  newest_section_enabled?: boolean;
  popular_section_enabled?: boolean;
  modules?: HomepageModule[];
}

export interface HomepageModulesResponse {
  type: 'custom_homepage_module';
  attributes: HomepageModule;
}

export interface HomepageResponse {
  type: 'homepage';
  attributes: HomepageAttributes;
}

/**
 * Type guard to check if a module is a content module
 */
export function isContentModule(
  module: HomepageModule
): module is ContentHomepageModule {
  return module.type === 'content';
}

/**
 * Type guard to check if a module is a banner module
 */
export function isBannerModule(
  module: HomepageModule
): module is BannerHomepageModule {
  return module.type === 'banner';
}

/**
 * Type guard to check if a module is a shortcuts module
 */
export function isShortcutsModule(
  module: HomepageModule
): module is ShortcutsHomepageModule {
  return module.type === 'shortcuts';
}
